/* global gapi */
import { useState, useCallback, useEffect, useContext, useMemo } from 'react';
import { shd } from '@shd/jslib/models';

import * as Sentry from '@sentry/browser';
import { errorUtil, strogging } from '@shd/jslib/infra';
import { AppHostContext } from '../../../services/appHost';
import { analytics } from '../../../services/analytics';
import axios from '../../../helpers/axios';
import { TeamContext } from '@services/Session';

interface AuthResponse {
  error?: string;
  code?: string;
  scope: string;
}

export interface AuthStatus {
  phase: 'previously-completed' | 'in-progress' | 'completed' | 'error';
  error?: {
    message: {
      title: string;
      subtitle?: string;
    };
    enum?: string;
    link?: string;
  };
  channel?: shd.PublishYT;
}

interface Props {
  onConnectionFinished?: (authStatus: AuthStatus) => void;
  liveTeamId?: string | null;
}

const useYoutubeConnection = ({
  onConnectionFinished,
  liveTeamId = null,
}: Props = {}) => {
  const teamStore = useContext(TeamContext);
  const teamId = liveTeamId ? liveTeamId : teamStore.id;
  const [status, setStatus] = useState<AuthStatus>();
  const appHost = useContext(AppHostContext);
  const appHostService = appHost.isHosted ? appHost.service : undefined;
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [showVideoSettings, setShowVideoSettings] = useState(false);

  const finalizeConnection = useCallback(
    (authStatus: AuthStatus) => {
      setStatus(authStatus);
      if (onConnectionFinished) {
        onConnectionFinished(authStatus);
      }
    },
    [onConnectionFinished]
  );

  const onUnlinkYoutubeClick = useCallback(() => {
    axios
      .delete('/api/youtube_auth', { data: { teamId } })
      .then(() => {
        setStatus({ phase: 'previously-completed' });
      })
      .catch((e) => {
        Sentry.captureException(e);
      });
  }, [teamId]);

  const updateUnlistedSetting = useCallback(
    (unlisted: boolean) => {
      axios
        .put('/api/youtube_auth', {
          teamId,
          data: {
            isUnlistedOnConclude: unlisted,
          },
        })
        .then(() => {
          if (status?.channel) {
            setStatus((prev) => {
              if (!prev?.channel) {
                return prev;
              }
              return {
                ...prev,
                channel: {
                  ...prev.channel,
                  isUnlistedOnConclude: unlisted,
                },
              };
            });
          }
          setShowPrivacyModal(false);
        })
        .catch((e) => {
          Sentry.captureException(e);
        });
    },
    [status?.channel, teamId]
  );

  // Fetch existing YouTube authorization on mount
  useEffect(() => {
    const fetchAuthStatus = async () => {
      try {
        const response = await axios.get('/api/youtube_auth', {
          params: { teamId },
        });
        setStatus({ channel: response.data, phase: 'previously-completed' });
      } catch (error) {
        Sentry.captureException(error);
      }
    };
    teamId && fetchAuthStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSignInSuccess = useCallback(
    async (response: AuthResponse) => {
      try {
        const redirectUri = appHost.isHosted
          ? 'https://sidelinehd.com'
          : window.location.origin;

        const postResponse = await axios.post('/api/youtube_auth', {
          ...response,
          redirectUri,
          teamId,
        });

        analytics.track('Streaming Destination Linked', {
          destinationType: 'YouTube',
          teamId,
        });

        finalizeConnection({ channel: postResponse.data, phase: 'completed' });
      } catch (error) {
        const errorMessage = errorUtil.errorMessage(error);
        strogging.error('useYoutubeConnection-apiYoutubeAuth-exception', {
          error: errorMessage,
        });

        finalizeConnection({
          error: {
            message: {
              title: 'Authorization Failed',
              subtitle: errorMessage,
            },
          },
          phase: 'error',
        });
      }
    },
    [teamId, appHost.isHosted, finalizeConnection]
  );

  const signIn = useCallback(async () => {
    setStatus({ phase: 'in-progress' });

    if (appHostService) {
      try {
        const response = await appHostService.requests.authorizeYoutube(teamId);
        await handleSignInSuccess(response);
      } catch (error) {
        const errorMessage = errorUtil.errorMessage(error);
        strogging.error('apphost-signIn', { error: errorMessage });

        finalizeConnection({
          error: {
            message: {
              title: 'Authorization Failed',
              subtitle: errorMessage,
            },
          },
          phase: 'error',
        });
      }
    } else {
      const config = {
        response_type: 'code',
        scope: 'https://www.googleapis.com/auth/youtube.force-ssl',
        client_id: import.meta.env.VITE_APP_GOOGLE_CLIENT_ID || '',
        access_type: 'offline',
        prompt: 'consent',
        cookiePolicy: 'single_host_origin',
      };

      try {
        const auth = await new Promise<typeof gapi.auth2>((resolve, reject) => {
          const timeout = setTimeout(
            () => reject(new Error('gapi.auth2 not loaded')),
            15000
          );
          window.gapi.load('auth2', () => {
            clearTimeout(timeout);
            resolve(gapi.auth2);
          });
        });

        auth.authorize(config, handleSignInSuccess);
      } catch (error) {
        const errorMessage = errorUtil.errorMessage(error);
        strogging.exception('useYoutubeConnection-signIn', {
          error: errorMessage,
        });

        finalizeConnection({
          error: {
            message: {
              title: 'Authorization Failed',
              subtitle: errorMessage,
            },
          },
          phase: 'error',
        });
      }
    }
  }, [appHostService, teamId, handleSignInSuccess, finalizeConnection]);

  const cancelSignin = useCallback(() => {
    strogging.log('useYoutubeConnection-cancelSignin');
    if (appHostService) {
      appHostService.notifications.userCancelledYoutubeAuth();
    }
  }, [appHostService]);

  return useMemo(
    () => ({
      signIn,
      status,
      cancelSignin,
      onUnlinkYoutubeClick,
      updateUnlistedSetting,
      showPrivacyModal,
      setShowPrivacyModal,
      showVideoSettings,
      setShowVideoSettings,
    }),
    [
      signIn,
      status,
      cancelSignin,
      onUnlinkYoutubeClick,
      updateUnlistedSetting,
      showPrivacyModal,
      showVideoSettings,
    ]
  );
};

export default useYoutubeConnection;
